import React, { useEffect, useState, useCallback } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faUser, faEdit, faSearch } from '@fortawesome/free-solid-svg-icons';
import "bootstrap/dist/css/bootstrap.min.css";
import { MultiSelect } from "primereact/multiselect";
import api from "../../../Authenticaltion/api";
import "./UserLeave.css";

const UserLeave = () => {
  const [filters, setFilters] = useState([]);
  const [employee, setEmployee] = useState([]);
  const [usersLeaves, setUsersLeaves] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);  // Show 5x4 grid
  const [showModal, setShowModal] = useState(false);
  const [editedLeave, setEditedLeave] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [employeeRes, leavesRes] = await Promise.all([
          api.get("/admin-employee"),
          api.get("/employeeleaveData")
        ]);
        setEmployee(employeeRes.data.data);
        setUsersLeaves(leavesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalPages = Math.ceil(usersLeaves.length / itemsPerPage);
  const indexOfLastUser = currentPage * itemsPerPage;
  const indexOfFirstUser = indexOfLastUser - itemsPerPage;
  const currentUsers = usersLeaves.slice(indexOfFirstUser, indexOfLastUser);

  const handleEdit = useCallback((leave) => {
    setEditedLeave({ ...leave });
    setShowModal(true);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setLoading(true);
      await api.post("/updateuserLeaveBalance", editedLeave);
      const res = await api.get("/employeeleaveData", {
        params: { employeeIds: filters }
      });
      setUsersLeaves(res.data);
      setShowModal(false);
    } catch (error) {
      console.error("Error updating leave:", error);
    } finally {
      setLoading(false);
    }
  }, [editedLeave, filters]);

  const handleInputChange = (e, field) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value) && (value === '' || parseInt(value) >= 0)) {
      setEditedLeave((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleEmployeeSelection = async (e) => {
    const selectedIds = [...new Set(e.value)];
    setCurrentPage(1);
    setFilters(selectedIds);
    try {
      setLoading(true);
      const res = await api.get("/employeeleaveData", {
        params: { employeeIds: selectedIds }
      });
      setUsersLeaves(res.data);
    } catch (error) {
      console.error("Error fetching employee leave data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="compact-dashboard mt-5">
      <div className="top-bar">
        <div className="search-box">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <MultiSelect
            value={filters}
            options={employee}
            onChange={handleEmployeeSelection}
            optionLabel="Name"
            optionValue="id"
            placeholder="Search employees..."
            className="compact-select"
            display="chip"
            filter
            filterBy="Name"
            showClear
          />
        </div>
        <div className="leave-legend">
          <span className="legend-item">
            <span className="value available">
              <FontAwesomeIcon icon={faCheckCircle} size="xs" />
              <span>Available</span>
            </span>
          </span>
          <span className="legend-item">
            <span className="value used">
              <FontAwesomeIcon icon={faTimesCircle} size="xs" />
              <span>Used</span>
            </span>
          </span>
        </div>
      </div>

      {loading ? (
        <div className="compact-spinner">
          <div className="spinner-ring"></div>
        </div>
      ) : (
        <>
          <div className="leave-compact-grid ">
            {currentUsers.map((user) => (
              <div key={user.id} className="compact-card">
                <div className="card-top">
                  <div className="user-brief">
                    <div className="user-avatar">
                      <FontAwesomeIcon icon={faUser} />
                    </div>
                    <span className="user-name">{user.Name}</span>
                  </div>
                </div>
                <div className="leave-list">
                  {user.leaves.map((leave) => (
                    <div key={leave.type} className="leave-item">
                      <div className="leave-info">
                        <span className="leave-label">{leave.type}</span>
                        <div className="leave-values">
                          <span className="value available">
                            <FontAwesomeIcon icon={faCheckCircle} size="xs" />
                            <span>{leave.remaining_balance}</span>
                          </span>
                          <span className="value used">
                            <FontAwesomeIcon icon={faTimesCircle} size="xs" />
                            <span>{leave.used}</span>
                          </span>
                          <button 
                            className="edit-icon"
                            onClick={() => handleEdit(leave)}
                            title="Edit Leave"
                          >
                            <FontAwesomeIcon icon={faEdit} size="xs" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="compact-pagination">
            <button 
              className="nav-btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="page-indicator">{currentPage} / {totalPages}</span>
            <button 
              className="nav-btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </>
      )}

      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        centered 
        className="compact-modal"
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-black">Edit Leave</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="compact-form">
            <Form.Group>
              <Form.Label>Leave Type</Form.Label>
              <Form.Control
                type="text"
                value={editedLeave.type || ''}
                readOnly
                className="compact-input"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Available Days</Form.Label>
              <Form.Control
                type="text"
                value={editedLeave.remaining_balance || ''}
                onChange={(e) => handleInputChange(e, 'remaining_balance')}
                className="compact-input"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserLeave;
